import React, { useEffect, useRef, useState } from "react";
import { api, ApiError, resolveUrl, type Property } from "../../api";
import { useToast } from "../../ToastContext";
import { GoogleMapView, loadGoogleMaps } from "../shared/GoogleMap";
import { PhotoLightbox } from "../shared/PhotoLightbox";
import { extractLocalityText } from "../shared/LocationCapture";
import { useLanguage } from "../../i18n";

function fmtINR(n: number | null | undefined) {
  if (n == null) return "—";
  return "₹" + Number(n).toLocaleString("en-IN");
}

const OCC_LABEL_KEYS: Record<string, string> = { boys: "occBoys", girls: "occGirls", "co-ed": "occCoEd" };

const FILTER_OPTIONS = [
  "All",
  "PG",
  "1RK",
  "1BHK",
  "2BHK",
  "3BHK",
  "4BHK",
  "5BHK",
  "6BHK",
  "Duplex",
  "Villa",
  "Shop",
  "Office",
  "Showroom",
  "Warehouse",
  "Co-working",
  "Factory",
];

const COMMERCIAL_TYPES = new Set(["Shop", "Office", "Showroom", "Warehouse", "Co-working", "Factory"]);

const HOUSE_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    <path d="M9 22V12h6v10" />
  </svg>
);
const PG_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 20h20M4 20V10l8-6 8 6v10M9 20v-6h6v6" />
  </svg>
);
const COMMERCIAL_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18M4 21V8l8-5 8 5v13M9 21v-5h6v5M9 12h.01M15 12h.01M9 8h.01M15 8h.01" />
  </svg>
);

function ListingIcon({ category, type }: { category: Property["category"]; type: string | null }) {
  const icon = category === "pg" ? PG_ICON : COMMERCIAL_TYPES.has(type ?? "") ? COMMERCIAL_ICON : HOUSE_ICON;
  return <div className="prop-thumb">{icon}</div>;
}

function listingGroup(p: Property): "pg" | "commercial" | "residential" {
  if (p.category === "pg") return "pg";
  return p.type && COMMERCIAL_TYPES.has(p.type) ? "commercial" : "residential";
}

const FILTER_LABEL_KEYS: Record<string, string> = {
  All: "filterAll",
  PG: "filterPg",
  "1RK": "filter1RK",
  "1BHK": "filter1BHK",
  "2BHK": "filter2BHK",
  "3BHK": "filter3BHK",
  "4BHK": "filter4BHK",
  "5BHK": "filter5BHK",
  "6BHK": "filter6BHK",
  Duplex: "filterDuplex",
  Villa: "filterVilla",
  Shop: "filterShop",
  Office: "filterOffice",
  Showroom: "filterShowroom",
  Warehouse: "filterWarehouse",
  "Co-working": "filterCoworking",
  Factory: "filterFactory",
};

function priceOf(p: Property): number {
  if (p.units?.length) {
    const rents = p.units.filter((u) => u.toLet).map((u) => u.expectedRent ?? Infinity);
    return rents.length > 0 ? Math.min(...rents) : Infinity;
  }
  if (p.category !== "pg") return p.expectedRent ?? Infinity;
  const beds = (p.rooms ?? []).map((r) => r.rentPerBed);
  return beds.length > 0 ? Math.min(...beds) : Infinity;
}

// Every rent a tenant could actually pay for this listing (one per vacant
// unit / PG bed) — a budget filter matches if ANY of them fits, so a
// building with a cheap studio and an expensive 3BHK shows up for both.
function rentsOf(p: Property): number[] {
  if (p.units?.length) return p.units.filter((u) => u.toLet && u.expectedRent != null).map((u) => u.expectedRent as number);
  if (p.category !== "pg") return p.expectedRent != null ? [p.expectedRent] : [];
  return (p.rooms ?? []).map((r) => r.rentPerBed);
}

const BUDGET_PRESETS: { min: string; max: string }[] = [
  { min: "", max: "5000" },
  { min: "5000", max: "10000" },
  { min: "10000", max: "20000" },
  { min: "20000", max: "40000" },
  { min: "40000", max: "" },
];

// Great-circle distance in km between two lat/lng points.
function distanceKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

function distanceOf(p: Property, me: { lat: number; lng: number } | null): number {
  if (!me || !p.location) return Infinity;
  return distanceKm(me, p.location);
}

const SORT_OPTIONS = [
  { value: "", labelKey: "sortDefault" },
  { value: "price_asc", labelKey: "sortPriceAsc" },
  { value: "price_desc", labelKey: "sortPriceDesc" },
  { value: "distance", labelKey: "sortDistance" },
];

export function TenantBrowse({ onNavigate }: { onNavigate?: (view: "plans") => void }) {
  const { t } = useLanguage();
  const [listings, setListings] = useState<Property[] | null>(null);
  const [selected, setSelected] = useState<Property | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [sort, setSort] = useState("");
  const [budgetMin, setBudgetMin] = useState("");
  const [budgetMax, setBudgetMax] = useState("");
  const [mapsKey, setMapsKey] = useState<string | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [myLocation, setMyLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [myLocationLabel, setMyLocationLabel] = useState("");
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState("");
  // How far from the tenant to look. 5 km by default once their location is known; "all" shows everything.
  const [radius, setRadius] = useState<"5" | "10" | "all">("5");
  const [configLoaded, setConfigLoaded] = useState(false);
  const autoLocated = useRef(false);
  const [mapSearchCenter, setMapSearchCenter] = useState<{ lat: number; lng: number; label: string } | null>(null);
  const mapSearchInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    api.get("/api/listings").then(({ listings }) => setListings(listings));
    api
      .get("/api/config")
      .then(({ googleMapsKey }) => setMapsKey(googleMapsKey))
      .catch(() => {})
      .finally(() => setConfigLoaded(true));
  }, []);

  // Opening Browse asks for the tenant's location straight away (once), so the first thing
  // they see is homes around them. If they say no, everything still shows, with a button to retry.
  useEffect(() => {
    if (!listings || !configLoaded || autoLocated.current || myLocation) return;
    autoLocated.current = true;
    findMyLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listings, configLoaded]);

  // Switching to map view should feel like it's centered on the tenant
  // straight away, not just on whatever listings happen to be pinned —
  // fires once per visit to map view rather than on every render (once
  // myLocation is set, this condition stops matching).
  useEffect(() => {
    if (showMap && !myLocation && !locating) findMyLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMap]);

  // Lets a tenant jump the map straight to an area they type — a locality,
  // landmark, or address — rather than only ever seeing whatever's already
  // pinned nearby. Same Places Autocomplete-on-a-plain-input pattern as the
  // owner's add-property location search (see LocationCapture.tsx).
  useEffect(() => {
    if (!showMap || !mapsKey) return;
    let cancelled = false;
    loadGoogleMaps(mapsKey)
      .then(() => {
        if (cancelled || !mapSearchInputRef.current) return;
        const autocomplete = new window.google.maps.places.Autocomplete(mapSearchInputRef.current, {
          fields: ["geometry", "formatted_address", "name"],
        });
        autocomplete.addListener("place_changed", () => {
          const place = autocomplete.getPlace();
          if (!place?.geometry?.location) return;
          setMapSearchCenter({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
            label: place.formatted_address || place.name || "",
          });
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [showMap, mapsKey]);

  function findMyLocation() {
    if (locating) return;
    if (!navigator.geolocation) {
      setLocError(t("tenantBrowse.locationNotSupported"));
      return;
    }
    setLocating(true);
    setLocError("");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setMyLocation({ lat, lng });
        // Raw coordinates come straight from the device's own GPS — Google
        // can't supply those. What Google's Geocoding API adds is turning
        // them into a human-readable locality, same as the owner's
        // "use current location" flow when adding a property.
        if (mapsKey) {
          try {
            await loadGoogleMaps(mapsKey);
            const geocoder = new window.google.maps.Geocoder();
            const result: any = await new Promise((resolve, reject) => {
              geocoder.geocode({ location: { lat, lng } }, (results: any, status: string) => {
                if (status === "OK" && results?.[0]) resolve(results[0]);
                else reject(new Error(status));
              });
            });
            setMyLocationLabel(extractLocalityText(result.address_components ?? []) || result.formatted_address);
          } catch {
            setMyLocationLabel("");
          }
        }
        setLocating(false);
      },
      (err) => {
        setLocError(err.code === err.PERMISSION_DENIED ? t("tenantBrowse.locationPermissionDenied") : t("tenantBrowse.locationCouldNotGet"));
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  if (!listings) return <div className="spinner" />;

  function applyUnlock(propertyId: string, patch: Partial<Property>) {
    setSelected((prev) => (prev ? { ...prev, ...patch } : prev));
    setListings((prev) => (prev ? prev.map((l) => (l.id === propertyId ? { ...l, ...patch } : l)) : prev));
  }

  if (selected) {
    return (
      <ListingDetail
        property={selected}
        listings={listings}
        mapsKey={mapsKey}
        onBack={() => setSelected(null)}
        onSelect={(p) => setSelected(p)}
        onUnlocked={(patch) => applyUnlock(selected.id, patch)}
        onNavigate={onNavigate}
      />
    );
  }

  const q = search.trim().toLowerCase();
  const minRent = budgetMin ? Number(budgetMin) : 0;
  const maxRent = budgetMax ? Number(budgetMax) : Infinity;
  const budgetActive = budgetMin !== "" || budgetMax !== "";
  const radiusKm = radius === "all" ? Infinity : Number(radius);
  const radiusActive = !!myLocation && radiusKm !== Infinity;
  const filtered = listings.filter((p) => {
    if (radiusActive && (!p.location || distanceKm(myLocation!, p.location) > radiusKm)) return false;
    if (q && !p.locality.toLowerCase().includes(q) && !p.label.toLowerCase().includes(q)) return false;
    if (budgetActive && !rentsOf(p).some((r) => r >= minRent && r <= maxRent)) return false;
    if (filter === "All") return true;
    if (filter === "PG") return p.category === "pg";
    return p.category !== "pg" && p.type === filter;
  });
  const sorted = [...filtered].sort((a, b) => {
    if (sort === "price_asc") return priceOf(a) - priceOf(b);
    if (sort === "price_desc") return priceOf(b) - priceOf(a);
    if (sort === "distance" || (sort === "" && radiusActive)) return distanceOf(a, myLocation) - distanceOf(b, myLocation);
    return 0;
  });
  const mappable = sorted.filter((p) => p.location);
  const activeFilterCount = (sort !== "" ? 1 : 0) + (budgetActive ? 1 : 0);
  // Nothing has narrowed the results yet — a browsing tenant, not a search
  // in progress — so lead with curated rows instead of one flat list.
  // Picking a chip, typing a search, or choosing a sort all fall through to
  // the plain filtered list below, same as before this was added.
  const showCurated = !q && filter === "All" && sort === "" && !budgetActive && !radiusActive;

  function chooseSort(v: string) {
    setSort(v);
    if (v === "distance" && !myLocation) findMyLocation();
  }

  return (
    <>
      <div className="section-title">{showCurated ? t("tenantBrowse.curatedHeading") : t("tenantBrowse.heading")}</div>
      <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
        <div className="input-wrap" style={{ flex: 1 }}>
          <input
            placeholder={t("tenantBrowse.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          className={`gps-btn${myLocation ? " active" : ""}${locating ? " locating" : ""}`}
          onClick={findMyLocation}
          disabled={locating}
          aria-label={t("tenantBrowse.useMyLocation")}
          title={t("tenantBrowse.useMyLocation")}
        >
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="3" />
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <div className="near-bar">
        <div className="near-bar-top">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 21s7-6.2 7-11.5A7 7 0 0 0 5 9.5C5 14.8 12 21 12 21Z" />
            <circle cx="12" cy="9.5" r="2.5" />
          </svg>
          <div className="near-bar-text">
            {myLocation ? (
              <>
                <b>{t("tenantBrowse.nearYou")}</b>
                {myLocationLabel ? <span> · {myLocationLabel}</span> : null}
              </>
            ) : locating ? (
              <span>{t("tenantBrowse.findingLocation")}</span>
            ) : (
              <span>{t("tenantBrowse.allowLocationPrompt")}</span>
            )}
          </div>
          {!locating && (
            <button type="button" className="near-bar-btn" onClick={findMyLocation}>
              {myLocation ? t("tenantBrowse.updateLocation") : t("tenantBrowse.useMyLocation")}
            </button>
          )}
        </div>
        <div className="near-bar-chips">
          {(["5", "10", "all"] as const).map((r) => (
            <button
              key={r}
              type="button"
              className={`chip${radius === r ? " selected" : ""}`}
              onClick={() => setRadius(r)}
            >
              {r === "all" ? t("tenantBrowse.allAreas") : t("tenantBrowse.withinKm", { km: r })}
            </button>
          ))}
        </div>
      </div>
      <div className="chip-row-scroll">
        {FILTER_OPTIONS.map((f) => (
          <div key={f} className={`chip${filter === f ? " selected" : ""}`} onClick={() => setFilter(f)}>
            {t(`tenantBrowse.${FILTER_LABEL_KEYS[f]}`)}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {mapsKey && (
          <button className="btn-outline btn-sm" onClick={() => setShowMap((v) => !v)}>
            {showMap ? t("tenantBrowse.listView") : t("tenantBrowse.mapView")}
          </button>
        )}
        <button
          className="btn-outline btn-sm"
          style={{ position: "relative", flexShrink: 0, width: "auto", padding: "0 16px" }}
          onClick={() => setShowFilters(true)}
        >
          {t("tenantBrowse.filtersBtn")}
          {activeFilterCount > 0 && <span className="filter-count-badge">{activeFilterCount}</span>}
        </button>
      </div>
      {locError && <div className="error-banner" style={{ marginBottom: 12 }}>{locError}</div>}
      {showFilters && (
        <FilterSheet
          sort={sort}
          setSort={chooseSort}
          budgetMin={budgetMin}
          budgetMax={budgetMax}
          setBudget={(min, max) => {
            setBudgetMin(min);
            setBudgetMax(max);
          }}
          onClear={() => {
            setSort("");
            setBudgetMin("");
            setBudgetMax("");
          }}
          onClose={() => setShowFilters(false)}
        />
      )}
      {listings.length === 0 && <div className="empty-state">{t("tenantBrowse.noVacancies")}</div>}
      {listings.length > 0 && sorted.length === 0 && (
        <div className="empty-state">
          {radiusActive ? t("tenantBrowse.noneNearby", { km: radius }) : t("tenantBrowse.noMatch")}
          {radiusActive && (
            <div style={{ marginTop: 12 }}>
              <button type="button" className="btn-outline btn-sm" onClick={() => setRadius("all")}>
                {t("tenantBrowse.showAllProperties")}
              </button>
            </div>
          )}
        </div>
      )}
      {showMap && mapsKey && sorted.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div className="input-wrap" style={{ marginBottom: 10 }}>
            <input ref={mapSearchInputRef} placeholder={t("tenantBrowse.searchLocationPlaceholder")} />
          </div>
          {mappable.length > 0 || mapSearchCenter ? (
            <GoogleMapView
              apiKey={mapsKey}
              me={myLocation}
              searchCenter={mapSearchCenter}
              markers={mappable.map((p) => ({
                id: p.id,
                lat: p.location!.lat,
                lng: p.location!.lng,
                title: p.label,
                subtitle: p.locality,
                photo: p.photos[0],
                price:
                  p.category !== "pg" && !p.units?.length
                    ? fmtINR(p.expectedRent)
                    : t("tenantBrowse.fromPrice", { price: fmtINR(priceOf(p)) }),
              }))}
              onMarkerClick={(id) => setSelected(sorted.find((p) => p.id === id) ?? null)}
            />
          ) : (
            <div className="empty-state">{t("tenantBrowse.noPinnedLocation")}</div>
          )}
        </div>
      )}
      {(!showMap || !mapsKey) && showCurated && (
        <CuratedFeed
          listings={listings}
          onSelect={setSelected}
          onBudgetSeeAll={() => chooseSort("price_asc")}
          onPgSeeAll={() => setFilter("PG")}
        />
      )}
      {(!showMap || !mapsKey) &&
        !showCurated &&
        sorted.map((p) => {
          const dist = myLocation && p.location ? distanceKm(myLocation, p.location) : null;
          return (
            <div className="prop-card" key={p.id} onClick={() => setSelected(p)} style={{ cursor: "pointer", marginBottom: 10 }}>
              {p.photos.length > 0 && (
                <img
                  src={resolveUrl(p.photos[0])}
                  alt=""
                  style={{ width: "100%", height: 140, objectFit: "cover", display: "block", background: "var(--brand-100)" }}
                />
              )}
              <div className="prop-top">
                <div className="prop-top-main">
                  <ListingIcon category={p.category} type={p.type} />
                  <div>
                    <div className="prop-addr">
                      {p.label}
                      {p.topListing && <span className="top-tag">★ {t("tenantBrowse.topTag")}</span>}
                      {!p.topListing && p.featured && <span className="featured-tag">{t("tenantBrowse.featuredTag")}</span>}
                    </div>
                    <div className="prop-locality">
                      {p.locality}
                      {dist != null
                        ? ` · ${t("tenantBrowse.distanceAway", { value: dist < 1 ? Math.round(dist * 1000) + " m" : dist.toFixed(1) + " km" })}`
                        : ""}
                    </div>
                  </div>
                </div>
              </div>
              <div className="prop-body">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span className="prop-chip facility">
                    {p.category === "pg"
                      ? t(`tenantBrowse.${OCC_LABEL_KEYS[p.occupancyType ?? ""] ?? "occDefault"}`)
                      : p.units?.length
                      ? t("tenantBrowse.housesCount", { count: p.units.filter((u) => u.toLet).length })
                      : p.type}
                  </span>
                  <span className="badge vacant">
                    {p.category !== "pg" && !p.units?.length
                      ? fmtINR(p.expectedRent)
                      : t("tenantBrowse.fromPrice", { price: fmtINR(priceOf(p)) })}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
    </>
  );
}

// One property card for a horizontally-scrolling rail — the curated feed's
// rows and "Similar properties" below both use this same card, so a listing
// looks identical whichever rail it happens to show up in.
function ListingRailCard({ property: p, onSelect }: { property: Property; onSelect: (p: Property) => void }) {
  const { t } = useLanguage();
  const price = priceOf(p);
  return (
    <div className="card" style={{ minWidth: 158, maxWidth: 158, cursor: "pointer", flexShrink: 0 }} onClick={() => onSelect(p)}>
      {p.photos[0] ? (
        <img
          src={resolveUrl(p.photos[0])}
          alt=""
          style={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 8, marginBottom: 8 }}
        />
      ) : (
        <div style={{ width: "100%", height: 90, borderRadius: 8, marginBottom: 8, background: "var(--surface-alt)" }} />
      )}
      <b style={{ fontSize: 12.5, display: "block", lineHeight: 1.3 }}>{p.label}</b>
      <div className="sub" style={{ fontSize: 11, marginTop: 2 }}>{p.locality}</div>
      <div style={{ fontWeight: 800, fontSize: 12.5, marginTop: 6, color: "var(--brand-700)" }}>
        {price === Infinity ? "—" : `${fmtINR(price)}${p.category === "pg" ? "/bed" : t("tenantBrowse.perMonthSuffix")}`}
      </div>
    </div>
  );
}

// The default "browsing" view: horizontally-scrolling curated rows instead
// of one flat list. Every row is driven by real listing data (topListing/
// featured flags, actual price, category) — a row with nothing in it just
// doesn't render, no placeholder curation.
function CuratedFeed({
  listings,
  onSelect,
  onBudgetSeeAll,
  onPgSeeAll,
}: {
  listings: Property[];
  onSelect: (p: Property) => void;
  onBudgetSeeAll: () => void;
  onPgSeeAll: () => void;
}) {
  const { t } = useLanguage();

  const topPicks = listings
    .filter((p) => p.topListing || p.featured)
    .sort((a, b) => (a.topListing === b.topListing ? 0 : a.topListing ? -1 : 1))
    .slice(0, 8);

  const budgetPicks = [...listings]
    .filter((p) => priceOf(p) !== Infinity)
    .sort((a, b) => priceOf(a) - priceOf(b))
    .slice(0, 8);
  const budgetCeiling = budgetPicks.length ? priceOf(budgetPicks[budgetPicks.length - 1]) : 0;

  const pgPicks = listings.filter((p) => p.category === "pg").slice(0, 8);

  const sections: { key: string; heading: string; items: Property[]; onSeeAll?: () => void }[] = [
    { key: "top", heading: t("tenantBrowse.curatedTopPicksHeading"), items: topPicks },
    {
      key: "budget",
      heading: t("tenantBrowse.curatedBudgetHeading", { amount: fmtINR(budgetCeiling) }),
      items: budgetPicks,
      onSeeAll: onBudgetSeeAll,
    },
    { key: "pg", heading: t("tenantBrowse.curatedPgHeading"), items: pgPicks, onSeeAll: onPgSeeAll },
  ].filter((s) => s.items.length > 0);

  return (
    <>
      {sections.map((s) => (
        <div key={s.key} style={{marginBottom: 22 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
            <div className="section-title" style={{margin: 0 }}>{s.heading}</div>
            {s.onSeeAll && (
              <button type="button" className="link-btn" style={{fontSize: 12.5 }} onClick={s.onSeeAll}>
                {t("tenantBrowse.curatedSeeAll")}
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 6 }}>
            {s.items.map((p) => (
              <ListingRailCard key={p.id} property={p} onSelect={onSelect} />
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

function FilterSheet({
  sort,
  setSort,
  budgetMin,
  budgetMax,
  setBudget,
  onClear,
  onClose,
}: {
  sort: string;
  setSort: (s: string) => void;
  budgetMin: string;
  budgetMax: string;
  setBudget: (min: string, max: string) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const digits = (v: string) => v.replace(/\D/g, "").slice(0, 7);
  const presetLabel = (pr: { min: string; max: string }) =>
    !pr.min
      ? t("tenantBrowse.budgetUnder", { amount: fmtINR(Number(pr.max)) })
      : !pr.max
        ? t("tenantBrowse.budgetOver", { amount: fmtINR(Number(pr.min)) })
        : `${fmtINR(Number(pr.min))} – ${fmtINR(Number(pr.max))}`;
  return (
    <>
      <div className="filter-backdrop" onClick={onClose} />
      <div className="filter-sheet">
        <div className="filter-sheet-handle" />
        <div className="filter-sheet-head">
          <div className="filter-sheet-title">{t("tenantBrowse.filtersTitle")}</div>
          <div className="filter-sheet-close" onClick={onClose}>✕</div>
        </div>

        <div className="filter-section-label">{t("tenantBrowse.sortByLabel")}</div>
        <div className="chip-row" style={{ marginBottom: 0 }}>
          {SORT_OPTIONS.map((o) => (
            <div
              key={o.value}
              className={`chip${sort === o.value ? " selected" : ""}`}
              onClick={() => setSort(o.value)}
            >
              {t(`tenantBrowse.${o.labelKey}`)}
            </div>
          ))}
        </div>

        <div className="filter-section-label" style={{ marginTop: 18 }}>{t("tenantBrowse.budgetLabel")}</div>
        <div className="chip-row" style={{ marginBottom: 10 }}>
          <div className={`chip${!budgetMin && !budgetMax ? " selected" : ""}`} onClick={() => setBudget("", "")}>
            {t("tenantBrowse.budgetAny")}
          </div>
          {BUDGET_PRESETS.map((pr) => (
            <div
              key={`${pr.min}-${pr.max}`}
              className={`chip${budgetMin === pr.min && budgetMax === pr.max ? " selected" : ""}`}
              onClick={() => setBudget(pr.min, pr.max)}
            >
              {presetLabel(pr)}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div className="input-wrap" style={{ flex: 1 }}>
            <input
              inputMode="numeric"
              value={budgetMin}
              onChange={(e) => setBudget(digits(e.target.value), budgetMax)}
              placeholder={t("tenantBrowse.budgetMinPlaceholder")}
            />
          </div>
          <span className="sub">–</span>
          <div className="input-wrap" style={{ flex: 1 }}>
            <input
              inputMode="numeric"
              value={budgetMax}
              onChange={(e) => setBudget(budgetMin, digits(e.target.value))}
              placeholder={t("tenantBrowse.budgetMaxPlaceholder")}
            />
          </div>
        </div>

        <div className="filter-sheet-footer">
          <button className="link-btn" onClick={onClear}>
            {t("tenantBrowse.clearAll")}
          </button>
          <button className="btn-solid btn-sm" style={{ flex: 1 }} onClick={onClose}>
            {t("tenantBrowse.showResults")}
          </button>
        </div>
      </div>
    </>
  );
}

function ListingDetail({
  property,
  listings,
  mapsKey,
  onBack,
  onSelect,
  onUnlocked,
  onNavigate,
}: {
  property: Property;
  listings: Property[];
  mapsKey: string | null;
  onBack: () => void;
  onSelect: (p: Property) => void;
  onUnlocked: (patch: Partial<Property>) => void;
  onNavigate?: (view: "plans") => void;
}) {
  const { t } = useLanguage();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [showMap, setShowMap] = useState(false);
  const [tenantPlan, setTenantPlan] = useState<string | null>(null);
  const [benchmark, setBenchmark] = useState<{ insufficientData: boolean; avgRent?: number } | null>(null);
  const address = property.location?.label ?? property.locality;

  useEffect(() => {
    api.get("/api/subscription").then((s) => setTenantPlan(s.plan)).catch(() => setTenantPlan("basic"));
  }, []);

  useEffect(() => {
    if (property.category === "pg" || !property.type) {
      setBenchmark(null);
      return;
    }
    const params = new URLSearchParams({ category: property.category, type: property.type });
    if (property.city) params.set("city", property.city);
    if (property.locality) params.set("locality", property.locality);
    api.get(`/api/rent-benchmark?${params.toString()}`).then(setBenchmark).catch(() => setBenchmark(null));
  }, [property.id, property.category, property.type, property.city, property.locality]);

  // Fire-and-forget — powers the owner's per-listing analytics (Premium).
  // Depends on property.id (not an empty dep array) so navigating between
  // listings via "similar properties" without unmounting still logs a view
  // for each one.
  useEffect(() => {
    api.post(`/api/listings/${property.id}/view`).catch(() => {});
  }, [property.id]);

  return (
    <>
      <button className="link-btn" style={{ marginBottom: 14 }} onClick={onBack}>
        {t("tenantBrowse.backToListings")}
      </button>
      <div className="reg-title">{property.label}</div>
      <div className="reg-sub">{property.locality}</div>

      {property.photos.length > 0 && (
        <div className="listing-gallery">
          {property.photos.map((src, i) => (
            <img
              key={i}
              className="listing-gallery-img"
              src={resolveUrl(src)}
              alt=""
              onClick={() => setLightboxIndex(i)}
            />
          ))}
        </div>
      )}
      {lightboxIndex !== null && (
        <PhotoLightbox photos={property.photos} index={lightboxIndex} onClose={() => setLightboxIndex(null)} />
      )}

      {property.category !== "pg" && property.units?.length ? (
        <>
          <div className="section-title" style={{ marginBottom: 8 }}>{t("tenantBrowse.housesHeading")}</div>
          {property.units.filter((u) => u.toLet).map((u) => (
            <div className="card" key={u.id}>
              <div className="card-row">
                <b>{u.label}</b>
                <span className={`badge ${u.tenancy ? "occupied" : "vacant"}`}>
                  {u.tenancy ? t("tenantBrowse.occupiedBadge") : t("tenantBrowse.vacantBadge")}
                </span>
              </div>
              <div className="card-row" style={{ marginTop: 8 }}>
                <span className="sub">{t("tenantBrowse.typeLabel")}</span>
                <b>{u.type}</b>
              </div>
              <div className="card-row" style={{ marginTop: 8 }}>
                <span className="sub">{t("tenantBrowse.rentLabel")}</span>
                <b>{fmtINR(u.expectedRent)}{t("tenantBrowse.perMonthSuffix")}</b>
              </div>
              <div className="card-row" style={{ marginTop: 8 }}>
                <span className="sub">{t("tenantBrowse.depositLabel")}</span>
                <b>{fmtINR(u.expectedDeposit)}</b>
              </div>
            </div>
          ))}
        </>
      ) : property.category !== "pg" ? (
        <>
          <div className="card">
            <div className="card-row">
              <span className="sub">{t("tenantBrowse.typeLabel")}</span>
              <b>{property.type}</b>
            </div>
            <div className="card-row" style={{ marginTop: 8 }}>
              <span className="sub">{t("tenantBrowse.rentLabel")}</span>
              <b>{fmtINR(property.expectedRent)}{t("tenantBrowse.perMonthSuffix")}</b>
            </div>
            <div className="card-row" style={{ marginTop: 8 }}>
              <span className="sub">{t("tenantBrowse.depositLabel")}</span>
              <b>{fmtINR(property.expectedDeposit)}</b>
            </div>
            {benchmark && !benchmark.insufficientData && benchmark.avgRent && property.expectedRent != null && (
              <div className="field-hint" style={{ marginTop: 8 }}>
                {property.expectedRent <= benchmark.avgRent
                  ? t("tenantBrowse.rentBenchmarkBelow", { amount: fmtINR(benchmark.avgRent) })
                  : t("tenantBrowse.rentBenchmarkAbove", { amount: fmtINR(benchmark.avgRent) })}
              </div>
            )}
          </div>
          {property.category === "commercial" && (
            <div className="card">
              <div className="sub" style={{ fontWeight: 800, marginBottom: 8 }}>{t("addPropertyForm.commercialDetailsHeading")}</div>
              {(property.carpetAreaSqft || property.builtUpAreaSqft) && (
                <div className="card-row">
                  <span className="sub">{t("addPropertyForm.carpetAreaLabel")}</span>
                  <b>
                    {property.carpetAreaSqft ? `${property.carpetAreaSqft} sq ft` : "—"}
                    {property.builtUpAreaSqft ? ` / ${property.builtUpAreaSqft} sq ft ${t("addPropertyForm.builtUpAreaLabel")}` : ""}
                  </b>
                </div>
              )}
              {property.lockInMonths != null && (
                <div className="card-row" style={{ marginTop: 8 }}>
                  <span className="sub">{t("addPropertyForm.lockInMonthsLabel")}</span>
                  <b>{property.lockInMonths}</b>
                </div>
              )}
              {property.rentEscalationPercent != null && (
                <div className="card-row" style={{ marginTop: 8 }}>
                  <span className="sub">{t("tenantBrowse.escalationLabel")}</span>
                  <b>{property.rentEscalationPercent}% / {property.rentEscalationYears ?? "—"} {t("tenantBrowse.yearsSuffix")}</b>
                </div>
              )}
              {property.possessionStatus && (
                <div className="card-row" style={{ marginTop: 8 }}>
                  <span className="sub">{t("addPropertyForm.possessionStatusLabel")}</span>
                  <b>{t(`addPropertyForm.possession_${property.possessionStatus}`)}</b>
                </div>
              )}
              {property.furnishingStatus && (
                <div className="card-row" style={{ marginTop: 8 }}>
                  <span className="sub">{t("editPropertyForm.furnishingStatusLabel")}</span>
                  <b>{t(`editPropertyForm.commercialFurnishing_${property.furnishingStatus}`)}</b>
                </div>
              )}
              {property.washroomType && (
                <div className="card-row" style={{ marginTop: 8 }}>
                  <span className="sub">{t("addPropertyForm.washroomTypeLabel")}</span>
                  <b>{t(`addPropertyForm.washroom_${property.washroomType}`)}</b>
                </div>
              )}
              {(property.seatsCount || property.cabinsCount || property.meetingRoomsCount) && (
                <div className="card-row" style={{ marginTop: 8 }}>
                  <span className="sub">{t("addPropertyForm.seatsCountLabel")}/{t("addPropertyForm.cabinsCountLabel")}/{t("addPropertyForm.meetingRoomsCountLabel")}</span>
                  <b>{property.seatsCount ?? 0} / {property.cabinsCount ?? 0} / {property.meetingRoomsCount ?? 0}</b>
                </div>
              )}
              {property.parkingSpots != null && property.parkingSpots > 0 && (
                <div className="card-row" style={{ marginTop: 8 }}>
                  <span className="sub">{t("addPropertyForm.parkingSpotsLabel")}</span>
                  <b>{property.parkingSpots}</b>
                </div>
              )}
              {property.powerLoadDetails && (
                <div className="card-row" style={{ marginTop: 8 }}>
                  <span className="sub">{t("addPropertyForm.powerLoadDetailsLabel")}</span>
                  <b>{property.powerLoadDetails}</b>
                </div>
              )}
              {(property.powerBackup || property.gstRegistrationPossible || property.frontageSignage) && (
                <div className="chip-row" style={{ marginTop: 10 }}>
                  {property.powerBackup && <div className="chip">{t("addPropertyForm.powerBackupChip")}</div>}
                  {property.gstRegistrationPossible && <div className="chip">{t("addPropertyForm.gstChip")}</div>}
                  {property.frontageSignage && <div className="chip">{t("addPropertyForm.frontageChip")}</div>}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="card">
            <div className="card-row">
              <span className="sub">{t("tenantBrowse.occupancyLabel")}</span>
              <b>{t(`tenantBrowse.${OCC_LABEL_KEYS[property.occupancyType ?? ""] ?? "occDefault"}`)}</b>
            </div>
            {property.mealPlan.length > 0 && (
              <div className="card-row" style={{ marginTop: 8 }}>
                <span className="sub">{t("tenantBrowse.mealPlanLabel")}</span>
                <b>{property.mealPlan.map((m) => t(`addPropertyForm.meal_${m}`)).join(", ")}</b>
              </div>
            )}
            {property.preferredTenants.length > 0 && (
              <div className="card-row" style={{ marginTop: 8 }}>
                <span className="sub">{t("tenantBrowse.preferredTenantsLabel")}</span>
                <b>{property.preferredTenants.map((tt) => t(`addPropertyForm.tenantType_${tt}`)).join(", ")}</b>
              </div>
            )}
          </div>
          {property.facilities.length > 0 && (
            <div className="chip-row">
              {property.facilities.map((f) => (
                <div className="chip" key={f}>
                  {t(`editPropertyForm.facility_${f}`)}
                </div>
              ))}
            </div>
          )}
          {(property.rooms ?? []).map((room) => (
            <div className="card" key={room.id}>
              <div className="card-row">
                <b>{room.sharingType}</b>
                <span className="badge vacant">{fmtINR(room.rentPerBed)}/bed</span>
              </div>
              <div className="sub" style={{ marginTop: 6 }}>
                {t("tenantBrowse.bedsVacant", {
                  vacant: room.beds.filter((b) => !b.tenancy && b.toLet).length,
                  total: room.beds.length,
                })}
              </div>
              {(room.hasAc || room.hasAttachedBathroom) && (
                <div className="chip-row" style={{ marginTop: 8 }}>
                  {room.hasAc && <div className="chip">{t("tenantBrowse.roomAc")}</div>}
                  {room.hasAttachedBathroom && <div className="chip">{t("tenantBrowse.roomAttachedBathroom")}</div>}
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {(address || property.location) && (
      <div className="card">
        <div className="sub" style={{ fontWeight: 800, marginBottom: 8 }}>{t("tenantBrowse.locationLabel")}</div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start", justifyContent: "space-between" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" style={{ flexShrink: 0, marginTop: 2, color: "var(--brand-700)" }}>
              <path d="M12 22s8-7.58 8-13A8 8 0 0 0 4 9c0 5.42 8 13 8 13Z" />
              <circle cx="12" cy="9" r="2.6" />
            </svg>
            <span style={{ fontSize: 13.5, fontWeight: 600, lineHeight: 1.4 }}>
              {address}
              {property.locationApproximate && (
                <span className="sub" style={{ display: "block", fontSize: 11.5, fontWeight: 600 }}>{t("confirmLocation.approximate")}</span>
              )}
            </span>
          </div>
          {property.location && mapsKey && (
            <button
              type="button"
              className="link-btn"
              style={{ flexShrink: 0, whiteSpace: "nowrap" }}
              onClick={() => setShowMap((v) => !v)}
            >
              {showMap ? t("tenantBrowse.hideMap") : t("tenantBrowse.viewOnMap")}
            </button>
          )}
        </div>
        {property.location && (
          <>
            {mapsKey && showMap ? (
              <div style={{ marginTop: 12 }}>
                <GoogleMapView
                  apiKey={mapsKey}
                  mode="pin"
                  height={160}
                  markers={[{ id: property.id, lat: property.location.lat, lng: property.location.lng, title: property.label }]}
                />
              </div>
            ) : null}
            {tenantPlan !== null && tenantPlan !== "pro" ? (
              <button
                type="button"
                className="btn-outline btn-sm"
                style={{ marginTop: 10, display: "block", width: "100%", textAlign: "center" }}
                onClick={() => onNavigate?.("plans")}
              >
                &#128274; {t("tenantBrowse.openInGoogleMaps")}
              </button>
            ) : (
              <a
                className="btn-outline btn-sm"
                style={{ marginTop: 10, display: "block", textAlign: "center", textDecoration: "none" }}
                href={`https://www.google.com/maps/search/?api=1&query=${property.location.lat},${property.location.lng}`}
                target="_blank"
                rel="noreferrer"
              >
                {t("tenantBrowse.openInGoogleMaps")}
              </a>
            )}
            {mapsKey && tenantPlan === "pro" && <NearbyPlaces location={property.location} mapsKey={mapsKey} />}
            {mapsKey && tenantPlan !== null && tenantPlan !== "pro" && (
              <div className="card" style={{ marginTop: 10, padding: "12px 14px" }}>
                <b style={{ fontSize: 12.5 }}>{t("tenantPlan.nearbyUpsellTitle")}</b>
                <div className="sub" style={{ marginTop: 2, marginBottom: 8 }}>{t("tenantPlan.nearbyUpsellBody")}</div>
                <button type="button" className="link-btn" onClick={() => onNavigate?.("plans")}>
                  {t("tenantPlan.nearbyUpsellBtn")}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      )}

      <ScheduleVisit property={property} />
      <OwnerContact property={property} onUnlocked={onUnlocked} onNavigate={onNavigate} />
      <SimilarProperties current={property} listings={listings} onSelect={onSelect} />
    </>
  );
}

const NEARBY_CATEGORIES = [
  { type: "transit_station", labelKey: "nearbyTransit" },
  { type: "supermarket", labelKey: "nearbyEssentials" },
  { type: "hospital", labelKey: "nearbyHealthcare" },
] as const;

// Legacy PlacesService only needs *a* div to attach to — it never renders
// anything into it, so a detached element (never appended to the DOM) is
// enough and avoids drawing a second, invisible map just for this lookup.
function NearbyPlaces({ location, mapsKey }: { location: { lat: number; lng: number }; mapsKey: string }) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Record<string, { name: string; distanceM: number }[]> | null>(null);

  useEffect(() => {
    if (!expanded || results || loading) return;
    let cancelled = false;
    setLoading(true);
    loadGoogleMaps(mapsKey)
      .then(() => {
        if (cancelled) return;
        const service = new window.google.maps.places.PlacesService(document.createElement("div"));
        const fetchCategory = (type: string) =>
          new Promise<{ name: string; distanceM: number }[]>((resolve) => {
            service.nearbySearch({ location, radius: 1500, type }, (res: any, status: string) => {
              if (status !== "OK" || !res) return resolve([]);
              resolve(
                res
                  .map((r: any) => ({
                    name: r.name as string,
                    distanceM: Math.round(
                      distanceKm(location, { lat: r.geometry.location.lat(), lng: r.geometry.location.lng() }) * 1000
                    ),
                  }))
                  .sort((a: { distanceM: number }, b: { distanceM: number }) => a.distanceM - b.distanceM)
                  .slice(0, 3)
              );
            });
          });
        Promise.all(NEARBY_CATEGORIES.map((c) => fetchCategory(c.type))).then((all) => {
          if (cancelled) return;
          const map: Record<string, { name: string; distanceM: number }[]> = {};
          NEARBY_CATEGORIES.forEach((c, i) => (map[c.labelKey] = all[i]));
          setResults(map);
          setLoading(false);
        });
      })
      .catch(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, mapsKey, location.lat, location.lng]);

  const hasAny = results && Object.values(results).some((r) => r.length > 0);

  return (
    <div style={{ marginTop: 10 }}>
      <button type="button" className="link-btn" onClick={() => setExpanded((v) => !v)}>
        {expanded ? t("tenantBrowse.hideNearby") : t("tenantBrowse.showNearby")}
      </button>
      {expanded && (
        <div style={{ marginTop: 8 }}>
          {loading && <div className="sub">{t("tenantBrowse.loadingNearby")}</div>}
          {!loading && results && !hasAny && <div className="sub">{t("tenantBrowse.nearbyEmpty")}</div>}
          {!loading &&
            results &&
            NEARBY_CATEGORIES.filter((c) => results[c.labelKey]?.length).map((c) => (
              <div key={c.labelKey} style={{ marginBottom: 8 }}>
                <div className="sub" style={{ fontWeight: 800, marginBottom: 4 }}>
                  {t(`tenantBrowse.${c.labelKey}`)}
                </div>
                {results[c.labelKey].map((place, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "3px 0" }}>
                    <span>{place.name}</span>
                    <span className="sub">{t("tenantBrowse.metersAway", { distance: place.distanceM })}</span>
                  </div>
                ))}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function SimilarProperties({
  current,
  listings,
  onSelect,
}: {
  current: Property;
  listings: Property[];
  onSelect: (p: Property) => void;
}) {
  const { t } = useLanguage();
  const similar = listings
    .filter((p) => p.id !== current.id && listingGroup(p) === listingGroup(current))
    .map((p) => ({
      p,
      sameCity: current.city && p.city === current.city ? 0 : 1,
      dist: current.location && p.location ? distanceKm(current.location, p.location) : Infinity,
    }))
    .sort((a, b) => {
      if (a.p.topListing !== b.p.topListing) return a.p.topListing ? -1 : 1;
      if (a.p.featured !== b.p.featured) return a.p.featured ? -1 : 1;
      return a.sameCity !== b.sameCity ? a.sameCity - b.sameCity : a.dist - b.dist;
    })
    .slice(0, 4)
    .map((x) => x.p);

  if (similar.length === 0) return null;

  return (
    <>
      <div className="section-title" style={{ marginTop: 4 }}>{t("tenantBrowse.similarPropertiesHeading")}</div>
      <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 6 }}>
        {similar.map((p) => (
          <ListingRailCard key={p.id} property={p} onSelect={onSelect} />
        ))}
      </div>
    </>
  );
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function ScheduleVisit({ property }: { property: Property }) {
  const { t } = useLanguage();
  const [date, setDate] = useState(todayStr());
  const [time, setTime] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState<{ date: string; time: string } | null>(null);
  const toast = useToast();

  async function submit() {
    if (!date || !time) return setError(t("tenantBrowse.pickDateTime"));
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/properties/${property.id}/visits`, { date, time, note });
      setSent({ date, time });
      toast(t("tenantBrowse.visitRequestSent"));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("tenantBrowse.couldNotSendVisit"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="sub" style={{ fontWeight: 800, marginBottom: 10 }}>{t("tenantBrowse.scheduleVisit")}</div>
      {error && <div className="error-banner">{error}</div>}
      {sent ? (
        <div className="empty-state" style={{ padding: "8px 0" }}>
          {t("tenantBrowse.requestedFor", {
            date: new Date(sent.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" }),
            time: sent.time,
          })}
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 10 }}>
            <div className="field" style={{ flex: 1 }}>
              <label>{t("tenantBrowse.dateLabel")}</label>
              <div className="input-wrap"><input type="date" min={todayStr()} value={date} onChange={(e) => setDate(e.target.value)} /></div>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>{t("tenantBrowse.timeLabel")}</label>
              <div className="input-wrap"><input type="time" value={time} onChange={(e) => setTime(e.target.value)} /></div>
            </div>
          </div>
          <div className="field">
            <label>{t("tenantBrowse.noteOptionalLabel")}</label>
            <div className="input-wrap"><input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("tenantBrowse.notePlaceholder")} /></div>
          </div>
          <button className="btn-solid" disabled={busy} onClick={submit}>
            {busy ? t("tenantBrowse.sending") : t("tenantBrowse.requestVisitBtn")}
          </button>
        </>
      )}
    </div>
  );
}

function OwnerContact({
  property,
  onUnlocked,
  onNavigate,
}: {
  property: Property;
  onUnlocked: (patch: Partial<Property>) => void;
  onNavigate?: (view: "plans") => void;
}) {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function unlock() {
    setBusy(true);
    try {
      const data = await api.post(`/api/listings/${property.id}/unlock`, {});
      onUnlocked({ ownerPhone: data.ownerPhone, ownerName: data.ownerName, phoneLocked: false });
      toast(t("tenantBrowse.contactUnlockedToast"));
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        onUnlocked({ unlockNeedsUpgrade: true });
      }
      toast(err instanceof ApiError ? err.message : t("tenantBrowse.couldNotUnlockToast"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-row">
        <div>
          <div className="sub">{t("tenantBrowse.ownerContactLabel")}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <b>{property.ownerName ?? "—"}</b>
            {property.ownerVerified && (
              <span style={{ color: "var(--good)", fontWeight: 800, fontSize: 12 }} title={t("tenantBrowse.verifiedOwnerBadge")}>
                &#10003; {t("tenantBrowse.verifiedOwnerBadge")}
              </span>
            )}
            {property.ownerReferralBadge && (
              <span style={{ color: "var(--brand-700)", fontWeight: 800, fontSize: 12 }} title={t("tenantBrowse.trustedOwnerBadge")}>
                ⭐ {t("tenantBrowse.trustedOwnerBadge")}
              </span>
            )}
          </div>
        </div>
      </div>
      {property.phoneLocked ? (
        property.unlockNeedsUpgrade ? (
          <div style={{ marginTop: 10 }}>
            <div className="sub" style={{ marginBottom: 8 }}>&#128222; XXXXX XXXXX</div>
            <b style={{ fontSize: 12.5 }}>{t("tenantBrowse.unlockUpsellTitle")}</b>
            <div className="sub" style={{ marginTop: 2, marginBottom: 8 }}>{t("tenantBrowse.unlockUpsellBody")}</div>
            <button type="button" className="btn-solid" onClick={() => onNavigate?.("plans")}>
              {t("tenantBrowse.unlockUpsellBtn")}
            </button>
          </div>
        ) : (
          <>
            <div className="sub" style={{ marginTop: 10 }}>&#128222; XXXXX XXXXX</div>
            <button className="btn-solid" style={{ marginTop: 10 }} disabled={busy} onClick={unlock}>
              {busy ? t("tenantBrowse.unlockingBtn") : t("tenantBrowse.unlockNumberFreeBtn")}
            </button>
          </>
        )
      ) : property.ownerPhone ? (
        <>
          <a
            className="btn-solid"
            style={{ marginTop: 10, display: "block", textAlign: "center", textDecoration: "none" }}
            href={`https://wa.me/91${property.ownerPhone}?text=${encodeURIComponent(
              t("tenantBrowse.whatsappMessage", { property: property.label })
            )}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("tenantBrowse.whatsappBtn")}
          </a>
          <a
            className="btn-outline"
            style={{ marginTop: 8, display: "block", textAlign: "center", textDecoration: "none" }}
            href={`tel:${property.ownerPhone}`}
          >
            &#128222; {t("tenantBrowse.callNumberBtn", { phone: property.ownerPhone })}
          </a>
        </>
      ) : null}
    </div>
  );
}
